import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {
  selectCurrentPlayer,
  selectElementalsConstructionTokens,
  selectElementalsPlanTokensByPlayerId,
  selectPhase
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {selectConstructionPlanEntries} from "../../../redux/reducers/privateStateReducer/selectors";
import './ElementalsConstructionTokensOverlay.css'
import {isPlayerFaction} from "../../../utils/factions/isPlayerFaction";
import {FACTIONS} from "../../../constants/factions";
import {PHASES} from "../../../constants/phases";
import {useIsCurrentUserTurn} from "../../../utils/gameState/user/useIsCurrentUserCurrentPlayer";
import {
  checkIfConstructionPlanningAvailable,
  getConstructionPlanPlayer
} from "../../../utils/gameState/construction/planning/getConstructionPlanContext";
import {
  buildConstructionPlanElementalsTokens
} from "../../../utils/gameState/construction/planning/buildConstructionPlanningViewState";
import ElementalsConstructionToken from "./ElementalsConstructionToken/ElementalsConstructionToken";
import {GAME_PANEL_MATERIALS, GAME_PANEL_SHAPES, GamePanel} from '../../gameUi/GamePanel';
import {joinClassNames} from '../../gameUi/joinClassNames';

// Элементов в ряду плашки (ElementalsConstructionTokensOverlay.css). Больше —
// уже не один ряд, и плашка компактнее (_compact): элементы мельче
const TOKENS_PER_ROW = 4;

/**
 * Выпавшие Элементалям элементы — только тому, кто за них играет:
 *  - в свой ход строительства — жетоны хода;
 *  - при планировании в чужой ход — вытянутые при входе в планирование, с
 *    потраченными на план элементами (как их видит проверка плана).
 * Соперникам и зрителям не показываются.
 */
const ElementalsConstructionTokensOverlay = () => {
  const currentPlayer = useSelector(selectCurrentPlayer);
  const elementalsConstructionTokens = useSelector(selectElementalsConstructionTokens);
  const phase = useSelector(selectPhase);
  const isCurrentUserTurn = useIsCurrentUserTurn();

  const isConstructionPlanning = useSelector(checkIfConstructionPlanningAvailable);
  const planPlayer = useSelector(getConstructionPlanPlayer);
  const planTokensByPlayerId = useSelector(selectElementalsPlanTokensByPlayerId);
  const planEntries = useSelector(selectConstructionPlanEntries);

  const isPlanningElementals = isConstructionPlanning && Boolean(planPlayer)
    && isPlayerFaction(planPlayer, FACTIONS.ELEMENTALS);
  const planTokens = isPlanningElementals ? planTokensByPlayerId[planPlayer.id] : null;
  const displayedPlanTokens = useMemo(
    () => (planTokens ? buildConstructionPlanElementalsTokens(planTokens, planEntries) : null),
    [planTokens, planEntries]
  );

  let tokens = [];
  if (isPlanningElementals) {
    tokens = displayedPlanTokens || [];
  } else if (phase === PHASES.CONSTRUCTION
    && Boolean(currentPlayer) && isPlayerFaction(currentPlayer, FACTIONS.ELEMENTALS)
    && isCurrentUserTurn
  ) {
    tokens = elementalsConstructionTokens || [];
  }

  if (tokens.length === 0) {
    return null;
  }

  return (
    // Светлый жетон над полем — как сводка найма (RecruitmentSummary)
    <GamePanel
      material={GAME_PANEL_MATERIALS.BONE}
      shape={GAME_PANEL_SHAPES.S}
      shadow={true}
      className={'elementals-construction-tokens-overlay_shadow'}
    >
      <div className={joinClassNames(
        'elementals-construction-tokens-overlay',
        tokens.length > TOKENS_PER_ROW && 'elementals-construction-tokens-overlay_compact',
      )}>
        {tokens.map((token, index) => (
          <ElementalsConstructionToken
            key={index}
            tokenName={token.tokenName}
            isUsed={token.isUsed}
          />
        ))}
      </div>
    </GamePanel>
  );
};

export default ElementalsConstructionTokensOverlay;
